/**
 * TierSelectionCard Component
 *
 * Displays the 3-tier waitlist options with analytics tracking.
 * - Free: Standard waitlist access
 * - Priority ($1): Priority + Beta access
 * - Founding Member ($5): Priority + Beta + 3 months Premium free
 */

import { Check, Crown, Star, Zap } from "lucide-react";
import { motion } from "motion/react";
import React, { useEffect, useState } from "react";

import { trackTierSelection, trackUpsellFunnel } from "../utils/analytics";

import { Card, CardContent } from "./ui/card";

export type WaitlistTier = "free" | "priority" | "founding";

interface TierOption {
  id: WaitlistTier;
  name: string;
  price: number;
  description: string;
  features: string[];
  icon: React.ElementType;
  popular?: boolean;
  highlight?: boolean;
}

const TIER_OPTIONS: TierOption[] = [
  {
    id: "free",
    name: "Free Waitlist",
    price: 0,
    description: "Join the standard waitlist",
    features: ["Standard waitlist access", "Email updates on launch"],
    icon: Star,
  },
  {
    id: "priority",
    name: "Priority Access",
    price: 1,
    description: "Get early access when we launch",
    features: [
      "Priority waitlist position",
      "Beta access at launch",
      "Early feature previews",
    ],
    icon: Zap,
    popular: true,
  },
  {
    id: "founding",
    name: "Founding Member",
    price: 5,
    description: "Become a founding member",
    features: [
      "Priority waitlist position",
      "Beta access at launch",
      "3 months Premium free",
      "Founding Member badge",
      "Exclusive early access",
    ],
    icon: Crown,
    highlight: true,
  },
];

interface TierSelectionCardProps {
  selectedTier: WaitlistTier;
  onTierSelect: (tier: WaitlistTier) => void;
  disabled?: boolean;
}

export function TierSelectionCard({
  selectedTier,
  onTierSelect,
  disabled = false,
}: TierSelectionCardProps) {
  const [hasTrackedView, setHasTrackedView] = useState(false);

  // Track when tier options are shown
  useEffect(() => {
    if (!hasTrackedView) {
      trackUpsellFunnel("offer_shown", { source: "waitlist_form" });
      setHasTrackedView(true);
    }
  }, [hasTrackedView]);

  const handleTierSelect = (tier: WaitlistTier) => {
    if (disabled) return;

    const tierOption = TIER_OPTIONS.find(t => t.id === tier);
    trackTierSelection(tier, {
      ...(tierOption?.price !== undefined ? { price: tierOption.price } : {}),
      source: "tier_card",
      previous_tier: selectedTier,
    });
    trackUpsellFunnel("tier_selected", {
      tier,
      ...(tierOption?.price !== undefined ? { value: tierOption.price } : {}),
    });

    onTierSelect(tier);
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Choose Your Access Level
        </h3>
        <p className="text-sm text-muted-foreground">
          Support Koru&apos;s development and get early access
        </p>
      </div>

      <div className="grid gap-4">
        {TIER_OPTIONS.map((tier, index) => {
          const Icon = tier.icon;
          const isSelected = selectedTier === tier.id;

          return (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                className={`
                  relative cursor-pointer transition-all duration-200
                  ${
                    isSelected
                      ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }
                  ${tier.highlight ? "shadow-lg" : ""}
                  ${disabled ? "opacity-50 cursor-not-allowed" : ""}
                `}
                onClick={() => handleTierSelect(tier.id)}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {tier.highlight && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-secondary text-secondary-foreground text-xs font-medium px-3 py-1 rounded-full">
                      Best Value
                    </span>
                  </div>
                )}

                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div
                        className={`
                        p-2 rounded-lg
                        ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}
                      `}
                      >
                        <Icon className="w-5 h-5" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <h4 className="font-semibold text-foreground">
                            {tier.name}
                          </h4>
                          <span className="text-lg font-bold text-primary">
                            {tier.price === 0 ? "Free" : `$${tier.price}`}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {tier.description}
                        </p>

                        <ul className="mt-3 space-y-1">
                          {tier.features.map(feature => (
                            <li
                              key={feature}
                              className="flex items-center text-sm text-muted-foreground"
                            >
                              <Check
                                className={`w-4 h-4 mr-2 ${isSelected ? "text-primary" : "text-muted-foreground"}`}
                              />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div
                      className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${
                        isSelected
                          ? "border-primary bg-primary"
                          : "border-muted-foreground"
                      }
                    `}
                    >
                      {isSelected && (
                        <Check className="w-3 h-3 text-primary-foreground" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {selectedTier !== "free" && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 p-3 bg-muted/50 rounded-lg text-center"
        >
          <p className="text-sm text-muted-foreground">
            💳 Payment will be processed securely. Your commitment helps us
            build Koru faster!
          </p>
        </motion.div>
      )}
    </div>
  );
}

export { TIER_OPTIONS };
export type { TierOption };
